import { getProtocolAdapter, ProtocolType } from './protocols';
import { UNIVERSAL_PROPERTIES, UniversalProperty } from './properties';

export interface UniversalTemplates {
  siteUiTemplates?: Record<string, any>;
  siteParamTemplates?: Record<string, any>;
}

export interface GroupUniversalUnitIds {
  bacnetUnits: string[];
  modbusUnits: string[];
}

// The API returns the ids as arrays, the Control screens replace them with id -> entry maps
const toIdList = (members: any): string[] => {
  if (Array.isArray(members)) {
    return members;
  }
  return Object.keys(members || {});
};

export const getGroupUniversalUnitIds = (group: any): GroupUniversalUnitIds => ({
  bacnetUnits: toIdList(group?.bacnetUnits),
  modbusUnits: toIdList(group?.modbusUnits),
});

export const getGroupMemberIds = (group: any): string[] => {
  const { bacnetUnits, modbusUnits } = getGroupUniversalUnitIds(group);
  // A BACnet/Modbus id can also be listed in `units`, count it once
  return Array.from(new Set([...(group?.units || []), ...bacnetUnits, ...modbusUnits]));
};

// Sorts selected member ids into the API's units/bacnetUnits/modbusUnits fields. Ids missing from
// `unitsMap` (e.g. a member that is currently hidden) stay in whichever field they came from.
export const splitGroupMembers = (
  ids: string[],
  unitsMap: Record<string, any>,
  currentMembers: GroupUniversalUnitIds = { bacnetUnits: [], modbusUnits: [] }
) => {
  const currentBacnet = new Set(currentMembers.bacnetUnits);
  const currentModbus = new Set(currentMembers.modbusUnits);
  const result = { units: [] as string[], bacnetUnits: [] as string[], modbusUnits: [] as string[] };
  ids.forEach((id) => {
    const protocol =
      unitsMap[id]?.protocol || (currentBacnet.has(id) ? 'bacnet' : currentModbus.has(id) ? 'modbus' : null);
    if (protocol === 'bacnet') {
      result.bacnetUnits.push(id);
    } else if (protocol === 'modbus') {
      result.modbusUnits.push(id);
    } else {
      result.units.push(id);
    }
  });
  return result;
};

// Mirrors the template resolution in useBacnetUniversalControls, without the per-unit fetch
export const resolveBacnetParamTemplate = (entry: any, templates: UniversalTemplates = {}) => {
  const { siteUiTemplates = {}, siteParamTemplates = {} } = templates;
  if (typeof entry?.paramTemplate === 'object' && entry?.paramTemplate !== null) {
    return entry.paramTemplate;
  }
  const uiTemplate =
    typeof entry?.uiTemplate === 'object' && entry?.uiTemplate !== null
      ? entry.uiTemplate
      : siteUiTemplates[entry?.uiTemplate];
  const paramTemplateId =
    typeof entry?.paramTemplate === 'string' ? entry.paramTemplate : uiTemplate?.paramTemplate;
  if (paramTemplateId && siteParamTemplates[paramTemplateId]) {
    return siteParamTemplates[paramTemplateId];
  }
  if (entry?.paramTemplateData) {
    return { entityTemplate: entry.paramTemplateData?.entityTemplate === true, data: entry.paramTemplateData };
  }
  return undefined;
};

export const getUniversalSourceData = (
  entry: any,
  protocol: ProtocolType,
  templates?: UniversalTemplates
) => {
  if (protocol === 'modbus') {
    return entry?.rows || entry?.paramTemplateData?.rows || [];
  }
  return resolveBacnetParamTemplate(entry, templates)?.data || entry?.paramTemplateData || {};
};

export const getUniversalEntryMappedPoints = (
  entry: any,
  protocol: ProtocolType,
  templates?: UniversalTemplates
): Record<string, any> =>
  getProtocolAdapter(protocol).getUniversalMappedPoints(getUniversalSourceData(entry, protocol, templates));

export interface UniversalGroupMember {
  id: string;
  name: string;
  protocol: ProtocolType;
  entry: any;
  mappedPoints: Record<string, any>;
}

export interface ScheduleMappingIssue {
  name: string;
  missing: UniversalProperty[];
}

// Checks which of the schedule's actions each BACnet/Modbus member can't carry out.
// `excluded` members have none of the required points, `partial` members lack only some.
export const getScheduleMappingIssues = (
  members: UniversalGroupMember[],
  requiredProperties: UniversalProperty[]
) => {
  const excluded: ScheduleMappingIssue[] = [];
  const partial: ScheduleMappingIssue[] = [];
  if (!requiredProperties.length) {
    return { excluded, partial };
  }
  members.forEach(({ name, mappedPoints }) => {
    const missing = requiredProperties.filter((property) => !mappedPoints[property]);
    if (!missing.length) {
      return;
    }
    if (missing.length === requiredProperties.length) {
      excluded.push({ name, missing });
    } else {
      partial.push({ name, missing });
    }
  });
  return { excluded, partial };
};

export const getScheduleRequiredProperties = ({
  hasPowerTime,
  hasSetpoint,
  hasFanMode,
}: {
  hasPowerTime: boolean;
  hasSetpoint: boolean;
  hasFanMode: boolean;
}): UniversalProperty[] => {
  const properties: UniversalProperty[] = [];
  if (hasPowerTime) {
    properties.push(UNIVERSAL_PROPERTIES.OPERATION_STATUS);
  }
  if (hasSetpoint) {
    properties.push(UNIVERSAL_PROPERTIES.SETPOINT);
  }
  if (hasFanMode) {
    properties.push(UNIVERSAL_PROPERTIES.FAN_MODE);
  }
  return properties;
};
